import React from "react";

/**
 * SelectControl — a standalone dropdown at the app's toolbar control geometry.
 *
 * For the one or two inline dropdowns that live in a strip — a date range beside a
 * `PageHeader`, a "show archived" switch next to a search box. A set of filters
 * worth a drawer is `FilterPanel`; a field inside that drawer is
 * `FilterSelectField`, which is taller by design and would make a toolbar row 8px
 * uneven.
 *
 * `onChange` receives the VALUE, not the event. `NativeSelect` is the event-first
 * primitive underneath that convention.
 *
 * The geometry matches `SearchInput` exactly — 36px, 10px radius, 0.875rem, and
 * the app-wide focus ring — because the two sit in the same strip.
 *
 * ⚠️ 10px is `tw-rounded-md` here; `tw-rounded-lg` would be 16px. The radius scale
 * is remapped in `tailwind.config.js`.
 *
 * Props:
 *   value      — controlled value
 *   onChange   — callback(nextValue)
 *   options    — [{ value, label }]
 *   label      — optional inline label, rendered BEFORE the control
 *   ariaLabel  — accessible name when there is no visible label
 *   className  — extra classes on the wrapper
 */
// `tw-border-solid` for the reason FormControls spells out: with Preflight off,
// a bare `tw-border` on a <select> inherits the UA's `inset` style and draws a
// bevel instead of a line.
const CONTROL =
  "tw-h-9 tw-pl-3 tw-pr-8 tw-text-sm tw-cursor-pointer tw-appearance-none " +
  "tw-border tw-border-solid tw-border-line tw-rounded-md " +
  "tw-bg-surface-raised tw-text-content tw-outline-none " +
  "tw-transition-[border-color,box-shadow] tw-duration-150 " +
  "focus:tw-border-primary/40 focus:tw-shadow-[0_0_0_0.2rem_hsl(var(--primary)/0.18)]";

const SelectControl = ({
  value,
  onChange,
  options = [],
  label,
  ariaLabel,
  className = "",
  disabled = false,
}) => (
  <div className={`tw-inline-flex tw-items-center tw-gap-2 ${className}`.trim()}>
    {label && (
      <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted tw-whitespace-nowrap">
        {label}
      </span>
    )}
    {/* The chevron is a background image rather than an overlaid icon so the
        control stays ONE element — an absolutely-positioned lucide chevron would
        need a relative wrapper per instance, and this renders inside toolbars
        that already flex. `appearance-none` removes the native one; without the
        replacement the control reads as a text input. */}
    <div className="tw-relative tw-inline-flex tw-items-center">
      <select
        className={CONTROL}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
        onChange={(e) => onChange?.(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <svg
        className="tw-pointer-events-none tw-absolute tw-right-2.5 tw-text-content-muted"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  </div>
);

export default SelectControl;
